import {
  resolvePrintAreaPx,
  resolveViewPhysicalSizesCm,
  safeParseAreaJson,
  type PrintAreaRect,
} from '../../../src/designer-app/utils/printArea';

export interface RulerMetricsInput {
  viewName: string;
  areas?: Record<string, unknown> | null;
  params?: Record<string, unknown> | null;
  stageWidth: number;
  stageHeight: number;
}

export interface RulerMetrics {
  printArea: PrintAreaRect;
  physicalCm: { width: number; height: number };
  pxPerCmX: number;
  pxPerCmY: number;
  /** False when the product view has no admin-entered "Printable size (cm)". */
  configured: boolean;
}

export function computeRulerMetrics(input: RulerMetricsInput): RulerMetrics {
  const stageWidth = Math.max(1, Number(input.stageWidth) || 1);
  const stageHeight = Math.max(1, Number(input.stageHeight) || 1);
  const rawArea = input.areas?.[input.viewName];
  const parsedArea = typeof rawArea === 'string'
    ? safeParseAreaJson(rawArea)
    : rawArea;
  const printArea = resolvePrintAreaPx(
    parsedArea as Record<string, unknown> | null,
    stageWidth,
    stageHeight,
  ) ?? { left: 0, top: 0, width: stageWidth, height: stageHeight };
  const configuredPhysical = resolveViewPhysicalSizesCm(input.params)[input.viewName];
  // Fallback exists only to keep pxPerCm finite; it is not a real measurement
  // and must never be drawn as a ruler scale (see RulersOverlay.vue).
  const physical = configuredPhysical ?? {
    width: Math.max(1, printArea.width),
    height: Math.max(1, printArea.height),
  };

  return {
    printArea,
    physicalCm: physical,
    pxPerCmX: printArea.width / Math.max(1, physical.width),
    pxPerCmY: printArea.height / Math.max(1, physical.height),
    configured: Boolean(configuredPhysical),
  };
}

/** Rounds a raw cm-per-tick value up to a "nice" 1/2/5 × 10^n step. */
export function niceTickStep(rawStepCm: number): number {
  if (!(rawStepCm > 0)) return 1;
  const exponent = Math.floor(Math.log10(rawStepCm));
  const base = 10 ** exponent;
  const fraction = rawStepCm / base;
  const niceFraction = fraction <= 1 ? 1 : fraction <= 2 ? 2 : fraction <= 5 ? 5 : 10;
  return niceFraction * base;
}

/** True when the product view has an admin-entered "Printable size (cm)". */
export function hasPrintableSize(params: Record<string, unknown> | null | undefined, viewName: string): boolean {
  return Boolean(resolveViewPhysicalSizesCm(params)[viewName]);
}
